<template>
  <EditableItem
    :terminal="true"
    :readonly="readonly"
    :hidden="hidden"
    :id="item.id"
    :name="name"
    placeholder="new property"
    :editing="editing"
    :attributes="attributes"
    :error="error"
    v-on:edit="$emit('edit')"
    v-on:delete="$emit('delete')"
    v-on:center="$emit('center', item.data_type)">
    <template v-if="editing">
      <div class="uk-margin">
        <label class="uk-flex uk-flex-column">
          <span class="uk-form-label">name <span class="uk-text-meta"> {{ error.field('/name') }}</span></span>
          <input
            ref="name"
            class="uk-form-controls uk-inline uk-input"
            type="text"
            pattern="[a-z](?:_?[a-z0-9]+)*"
            :placeholder="$command('objects.name', { label: 'property' })"
            autofocus
            v-on:change="$emit('update', {field: 'name', value: name.trim().replace(/[\s-()+\.]/g, '_').replace(/__+/g, '_').toLowerCase()})"
            v-model="name"
            spellcheck="false"
            autocorrect="off"
            autocapitalize="off"
            autocomplete="off"
            v-bind:disabled="readonly">
          <p class="uk-margin-left uk-margin-small-top uk-text-right uk-text-meta
                uk-margin-remove-bottom uk-animation-fade" v-if="help">
            Enter a short, unique, and memorable name. The name must start with a
            lowercase letter ("a" to "z"), and can contain lowercase letters, numbers
            ("0" to "9"), with an underscore ("_") between words.
          </p>
        </label>
      </div>
      <div class="uk-margin">
        <div class="uk-form-label">data type <span class="uk-text-meta"> {{ error.field('/data_type') }}</span></div>
        <div class="uk-form-controls">
          <Attribute :version="version"
            :context="context"
            :label="$label('objects.data_type')"
            :primitive="true"
            :required="true"
            :value="item.data_type"
            v-on:update="$emit('update', {field: 'data_type', value: $event, link: true})"
            v-on:center="$emit('center', $event)"
            :readonly="readonly">
          </Attribute>
        </div>
        <p class="uk-margin-small-top uk-text-right uk-text-meta
              uk-margin-remove-bottom uk-animation-fade" v-if="help">
          Select a data type for this property
        </p>
      </div>
      <div class="uk-margin">
        <label>
          <input class="uk-radio" type="radio" value="0" v-model="cardinality"
            v-on:change="$emit('update', {field: 'cardinality', value: '0'})" v-bind:disabled="readonly">
          <span class="uk-margin-small-left uk-margin-small-right uk-form-label" style="display: inline-block">optional</span>
          <p class="uk-margin-remove-top uk-text-right uk-text-meta
                uk-margin-remove-bottom uk-animation-fade" v-if="help">
            Optional fields can have one or no value
          </p>
        </label>
        <label>
          <input class="uk-radio" type="radio" value="1" v-model="cardinality"
            v-on:change="$emit('update', {field: 'cardinality', value: '1'})" v-bind:disabled="readonly">
          <span class="uk-margin-small-left uk-margin-small-right uk-form-label" style="display: inline-block">required</span>
          <p class="uk-margin-remove-top uk-text-right uk-text-meta
                uk-margin-remove-bottom uk-animation-fade" v-if="help">
            Required fields have exactly one value
          </p>
        </label>
        <label>
          <input class="uk-radio" type="radio" value="" v-model="cardinality"
            v-on:change="$emit('update', {field: 'cardinality', value: null})" v-bind:disabled="readonly">
          <span class="uk-margin-small-left uk-margin-small-right uk-form-label" style="display: inline-block">collection</span>
          <p class="uk-margin-remove-top uk-text-right uk-text-meta
                uk-margin-remove-bottom uk-animation-fade" v-if="help">
            Collection fields can have multiple values
          </p>
        </label>
      </div>
      <div class="uk-margin" v-if="custom">
        <div class="uk-form-label">metadata</div>
        <div class="uk-form-controls">
          <TypeMetadata :version="version"
            :context="context"
            :typeid="item.data_type"
            :special="special"
            :metadata="metadata"
            :help="help"
            :readonly="readonly"
            v-on:update="$emit('update', $event)"
            v-on:center="$emit('center', $event)">
          </TypeMetadata>
        </div>
      </div>
      <div class="uk-margin">
        <label class="uk-flex uk-flex-column">
          <div class="uk-form-label">description <span class="uk-text-meta"> {{ error.field('/description') }}</span></div>
          <textarea
            ref="description"
            class="uk-form-controls uk-inline uk-textarea uk-width-expand uk-flex-auto"
            style="resize: vertical"
            rows="5"
            v-model="description"
            :placeholder="$command('objects.description', { label: 'property' })"
            v-on:change="$emit('update', {field: 'description', value: description})"
            v-bind:disabled="readonly">
          </textarea>
          <p class="uk-margin-left uk-margin-small-top uk-text-right uk-text-meta
                uk-margin-remove-bottom uk-animation-fade" v-if="help">
            Enter a brief summary of the data contained in this property. This information
            helps users understand how to interpret the field.
          </p>
        </label>
      </div>
    </template>
    <p v-else-if="description">{{ description }}</p>
    <p class="uk-text-italic uk-text-center" v-else>this property has no description</p>
  </EditableItem>
</template>

<script>
import { mapState } from 'vuex';
import EditableItem from '@/components/canvas/EditableItem.vue';
import Attribute from '@/components/canvas/entities/Attribute.vue';
import TypeMetadata from '@/components/canvas/entities/TypeMetadata.vue';

function assign(src, dst) {
  const cardinality = src.item.cardinality ?? '';
  const attributes = [];
  dst.name = src.item.name || '';
  dst.description = src.item.description || '';
  dst.cardinality = cardinality;
  if (src.item.special && src.item.special !== dst.special?.value) dst.special = {
    value: src.item.special,
    typeid: src.item.data_type,
  };
  else if (!src.item.special && dst.special?.typeid === src.item.data_type) dst.special = null;
  if (src.item.metadata && src.item.metadata !== dst.metadata?.value) dst.metadata = {
    value: src.item.metadata,
    typeid: src.item.data_type,
  };
  else if (!src.item.metadata && dst.metadata?.typeid === src.item.data_type) dst.metadata = null;

  dst.custom = (dst.special && dst.special.typeid === src.item.data_type)
    || (dst.metadata && dst.metadata.typeid === src.item.data_type)
    || src.item.data_type === 'STRING'
    || src.item.data_type === 'INTEGER'
    || src.item.data_type === 'FLOAT'
    || src.item.data_type === 'DECIMAL'
    || src.item.data_type === 'DATETIME'
    || src.item.data_type === 'LOOKUP'
    || src.item.data_type === 'ATTACHMENT';
  dst.hidden = !src.item.data_type || src.context.isPrimitive(src.item.data_type);
  if (src.item.data_type) {
    let label = src.context.label(src.item.data_type);
    if (src.context.isPrimitive(src.item.data_type))
    {
      if (dst.special && dst.special.typeid == src.item.data_type)
      {
        label = `${label}(${dst.special.value})`;
      }
      if (dst.metadata && dst.metadata.typeid == src.item.data_type)
      {
        label = `${label} ${dst.metadata.value}`;
      }
    }
    switch (cardinality) {
      case '0':
        attributes.push(`${label} (optional)`);
        break;
      case '1':
        attributes.push(label);
        break;
      default:
        attributes.push(`Collection of ${label}`);
        break;
    }
  } else {
    attributes.push('awaiting data type');
  }
  dst.attributes = attributes;

  dst.error = {
    invalid: src.errors.length,
    msg: src.errors.length ? 'invalid data' : null,
    local: [],
    fields: new Map(),
    field(src) {
      return this.fields.get(src) ||'';
    },
  }
  for (const {property, detail} of src.errors) {
    switch (property) {
      case '/name':
      case '/data_type':
      case '/description':
        dst.error.fields.set(property, detail);
        break;
      default:
        dst.error.local.push(detail);
        break;
    }
  }
  return dst;
}

export default {
  name: 'Property',
  props: {
    version: Number,
    item: Object,
    errors: Array,
    editing: Boolean,
    context: Object,
    categories: Object,
    attribute: String,
    readonly: Boolean,
  },
  components: {
    EditableItem,
    Attribute,
    TypeMetadata,
  },
  data() {
    return assign(this, {special: null, metadata: null});
  },
  mounted() {
    if (this.validate) {
      this.$refs.description.reportValidity();
      this.$refs.name.reportValidity();
    }
  },
  computed: {
    ...mapState('api', ['help']),
  },
  watch: {
    version() {
      assign(this, this);
    },
    validate() {
      if (this.validate) {
        this.$refs.description.reportValidity();
        this.$refs.name.reportValidity();
      }
    },
  }
}
</script>